import { Injectable } from '@angular/core';
import { CTVModel } from '../Models/ctv-model';

/**
 * StepCopyService - Manages copy/paste operations for Command Steps
 * 
 * This service uses localStorage for temporary storage of copied steps.
 * Data is stored as JSON strings in localStorage and is scoped to the current project.
 * 
 * Storage Keys:
 * - 'copiedSteps': Array of copied CTVModel objects (as JSON string)
 * - 'copiedStepsProjectId': The project ID where steps were copied from
 * 
 * Security Note: While localStorage persists data, it's only accessible within
 * the same browser/domain and automatically clears when the project changes.
 */

@Injectable({
  providedIn: 'root'
})
export class CommandStepCopyService {

   // localStorage keys
  private readonly STORAGE_KEY_STEPS = 'copiedSteps';
  private readonly STORAGE_KEY_PROJECT = 'copiedStepsProjectId';

  constructor() { }

   /**
   * Copy selected command steps to localStorage
   * 
   * WHAT GETS COPIED:
   * - All properties of the CTVModel objects (command, target, value, etc.)
   * - Command details (name, _id, accepts)
   * - Target objects (WebObject or ApiObject with all locators)
   * - Values (value1, value2, including secure flags)
   * - Active/inactive status
   * - Error and warning states
   * 
   * WHAT DOES NOT GET COPIED:
   * - The unique _id (set to null for new instances)
   * - The 'selected' state (cleared)
   * - The 'deleted' flag (reset to 0)
   * - Any temporary UI states
   * 
   * HOW IT WORKS:
   * 1. Takes the array of selected CTVModel objects
   * 2. Deep clones them using JSON.stringify/parse to avoid reference issues
   * 3. Stores the cloned data in localStorage as a JSON string
   * 4. Stores the project ID to ensure paste only works in same project
   * 
   * @param steps Array of CTVModel objects to copy
   * @param projectId Current project/step ID for scoping
   */

   copySteps(steps: CTVModel[], projectId: string): void {
    try {
      // Step 1: Deep clone the steps to avoid reference issues
      // JSON.stringify converts objects to string
      // JSON.parse converts string back to objects (creates new instances)
      const clonedSteps = JSON.parse(JSON.stringify(steps));
      
      // Step 2: Clean up the cloned data
      clonedSteps.forEach(step => {
        // Remove unique identifiers - new steps need new IDs
        step._id = null;
        step.commandSteps_id = null;
        
        // Clear selection state
        (step as any).selected = false;
        
        // Ensure step is not marked as deleted
        step.deleted = 0;
        
        // Clear any temporary UI states
        delete (step as any).pasted;
        delete (step as any).showValue;
        delete (step as any).showValue2;
      });
      
      // Step 3: Convert to JSON string and store in localStorage
      localStorage.setItem(this.STORAGE_KEY_STEPS, JSON.stringify(clonedSteps));
      localStorage.setItem(this.STORAGE_KEY_PROJECT, projectId);
      
    } catch (error) {
      // Fallback: clear storage if there's an error
      this.clearClipboard();
    }
  }

   /**
   * Get copied steps from localStorage if they belong to the current project
   * 
   * HOW RETRIEVAL WORKS:
   * 1. Checks if the current project matches the stored project ID
   * 2. If match, retrieves JSON string from localStorage
   * 3. Parses JSON string back into CTVModel objects
   * 4. Returns deep cloned copy (so original clipboard data stays intact)
   * 
   * WHY DEEP CLONE ON RETRIEVAL:
   * - Allows pasting the same steps multiple times
   * - Each paste gets independent objects
   * - Modifications to pasted steps don't affect clipboard
   * 
   * @param projectId Current project/step ID
   * @returns Array of copied CTVModel objects or empty array
   */
  getCopiedSteps(projectId: string): CTVModel[] {
    try {
      // Step 1: Check project scope
      const storedProjectId = localStorage.getItem(this.STORAGE_KEY_PROJECT);
      
      if (storedProjectId !== projectId) {
        return [];
      }
      
      // Step 2: Retrieve JSON string from localStorage
      const stepsJson = localStorage.getItem(this.STORAGE_KEY_STEPS);
      
      if (!stepsJson) {
        return [];
      }
      
      // Step 3: Parse JSON string to objects
      const steps = JSON.parse(stepsJson);
      
      // Step 4: Return deep clone (allows multiple pastes)
      const clonedSteps = JSON.parse(JSON.stringify(steps));
  
      return clonedSteps;
      
    } catch (error) {
      this.clearClipboard();
      return [];
    }
  }

  /**
   * Check if there are copied steps available for the current project
   * 
   * @param projectId Current project/step ID
   * @returns true if steps are available for paste
   */
  hasCopiedSteps(projectId: string): boolean {
    const storedProjectId = localStorage.getItem(this.STORAGE_KEY_PROJECT);
    const stepsJson = localStorage.getItem(this.STORAGE_KEY_STEPS);
   
    const hasSteps = storedProjectId === projectId && !!stepsJson;
    
    return hasSteps;
  }

   /**
   * Clear clipboard when switching projects
   * 
   * HOW PROJECT SCOPING WORKS:
   * 1. When project changes, this method is called
   * 2. Compares new project ID with stored project ID
   * 3. If different, clears the clipboard
   * 4. Prevents pasting steps from one project into another
   * 
   * @param newProjectId New project ID after switch
   */
  onProjectSwitch(newProjectId: string): void {
    const storedProjectId = localStorage.getItem(this.STORAGE_KEY_PROJECT);
    
    if (storedProjectId && storedProjectId !== newProjectId) {
      this.clearClipboard();
    }
  }

  getCurrentProjectId(): string {
    return localStorage.getItem("projectId") || '';
  }

  /**
   * Clear the clipboard by removing data from localStorage
   * 
   * WHEN THIS IS CALLED:
   * - On project switch
   * - On error during copy/paste
   * - When user explicitly clears selection
   */
  clearClipboard(): void {
    localStorage.removeItem(this.STORAGE_KEY_STEPS);
    localStorage.removeItem(this.STORAGE_KEY_PROJECT);
    }

   /**
   * Get diagnostic information about clipboard state (for debugging)
   * 
   * @returns Object with clipboard state information
   */
  getClipboardInfo(): any {
    const stepsJson = localStorage.getItem(this.STORAGE_KEY_STEPS);
    const projectId = localStorage.getItem(this.STORAGE_KEY_PROJECT);
    
    let stepCount = 0;
    if (stepsJson) {
      try {
        const steps = JSON.parse(stepsJson);
        stepCount = Array.isArray(steps) ? steps.length : 0;
      } catch (e) {
        // Invalid JSON
      }
    }
    
    return {
      hasData: !!stepsJson,
      projectId: projectId,
      stepCount: stepCount,
      storageKeys: {
        steps: this.STORAGE_KEY_STEPS,
        project: this.STORAGE_KEY_PROJECT
      }
    };
  }
}
